/**
 * PhenotypeTwinPage — iter609
 * Hybrid discovery: 7 quick questions + автоматичен lab/body анализ →
 * метаболитен фенотип + Claude обяснение на български.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronRight, Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import { twinLabel } from "@/lib/twinCatalog";

import { MobileTopBar } from "@/components/MobileTopBar";
export default function PhenotypeTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(null);
  const [catalogue, setCatalogue] = useState({});
  const [result, setResult] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [explaining, setExplaining] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/phenotype-twin/questions`),
      axios.get(`${API}/phenotype-twin/me`),
    ])
      .then(([q, me]) => {
        setQuestions(q.data.questions || []);
        setCurrent(me.data.current);
        setCatalogue(me.data.catalogue || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  const submitDiscovery = async () => {
    setDiscovering(true);
    setResult(null);
    setExplanation("");
    try {
      const { data } = await axios.post(`${API}/phenotype-twin/discover`, { answers });
      setResult(data);
      toast.success("Фенотипът е идентифициран");
      // Auto-fetch Claude explanation
      setExplaining(true);
      try {
        const r = await axios.post(`${API}/phenotype-twin/explain`, {
          phenotype_id: data.phenotype_id,
        }, { timeout: 35000 });
        setExplanation(r.data.explanation_bg || "");
      } catch { /* fallback ok */ }
      setExplaining(false);
    } catch (e) {
      toast.error("Грешка при анализа");
    }
    setDiscovering(false);
  };

  const cardCls = isDark
    ? "bg-slate-900/60 border-slate-800"
    : "bg-white border-slate-200";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="phenotype-twin-page">
      <MobileTopBar title="Наследственост" fallbackTo="/dashboard" testid="phenotype-twin-mobile-topbar" />
        <Loader2 className="w-6 h-6 animate-spin text-fuchsia-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="phenotype-twin-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* iter1017 — профилът захранва близнака */}
        <ProfileContextStrip domain="phenotype" isDark={isDark} className="mb-4" />
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-fuchsia-500" />
          <h1 className="text-xl font-bold">🧬 {twinLabel("phenotype")}</h1>
        </div>
        <p className="text-sm opacity-70 mb-6">
          Откриваме метаболитния ти фенотип, комбинирайки твоите лаборатории и тяло-данни с 7 кратки въпроса.
        </p>

        {/* Current phenotype (if exists) */}
        {current && !result && (
          <div className={`p-4 rounded-2xl border mb-4 ${cardCls}`} data-testid="current-phenotype">
            <div className="text-[10px] uppercase opacity-50 mb-1">Текущ профил</div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{current.icon}</span>
              <h2 className="text-lg font-bold" style={{ color: current.color }}>{current.label_bg}</h2>
            </div>
            <p className="text-xs opacity-70 mt-2">{current.summary_bg}</p>
            <div className="text-[10px] opacity-50 mt-1">Confidence: {current.confidence_pct}%</div>
          </div>
        )}

        {/* Questions */}
        {!result && (
          <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="questions-section">
            <h2 className="font-bold text-sm mb-3">Отговори с интензивност (0=не, 3=силно)</h2>
            <div className="space-y-3">
              {questions.map((q) => (
                <div key={q.id} data-testid={`question-${q.id}`}>
                  <p className="text-sm mb-1.5">{q.text}</p>
                  <div className="flex gap-2">
                    {[0, 1, 2, 3].map((v) => (
                      <button
                        key={v}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: v }))}
                        data-testid={`answer-${q.id}-${v}`}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                          answers[q.id] === v
                            ? "bg-fuchsia-500 text-white"
                            : isDark
                              ? "bg-slate-800 text-slate-400 hover:bg-slate-700"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {v === 0 ? "Не" : v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button
              onClick={submitDiscovery}
              disabled={discovering}
              data-testid="discover-btn"
              className="w-full mt-4 bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white"
            >
              {discovering ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {discovering ? "Анализирам..." : "Открий моя фенотип"} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="result-section">
            <div className="text-[10px] uppercase opacity-50 mb-1">Резултат · {result.confidence_pct}% confidence</div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-3xl">{result.phenotype.icon}</span>
              <h2 className="text-xl font-bold" style={{ color: result.phenotype.color }}>
                {result.phenotype.label_bg}
              </h2>
            </div>
            <p className="text-sm opacity-80 mt-2">{result.phenotype.summary_bg}</p>

            <div className="mt-4">
              <div className="text-[10px] uppercase opacity-50 mb-1.5">Ключови интервенции</div>
              <ul className="space-y-1.5">
                {result.phenotype.key_levers_bg.map((lever, i) => (
                  <li key={i} className="text-sm flex gap-2" data-testid={`lever-${i}`}>
                    <span className="text-emerald-500 flex-shrink-0">✓</span>{lever}
                  </li>
                ))}
              </ul>
            </div>

            {explaining && (
              <div className="mt-4 p-3 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/20 flex items-center gap-2 text-xs">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-fuchsia-500" />
                AI анализира профила ти...
              </div>
            )}
            {explanation && (
              <div className={`mt-4 p-3 rounded-lg ${isDark ? "bg-slate-800/40" : "bg-slate-100"}`} data-testid="ai-explanation">
                <div className="text-[10px] uppercase opacity-50 mb-1.5">✨ AI обяснение</div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{explanation}</p>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => { setResult(null); setAnswers({}); }} data-testid="reset-btn">
                Започни отново
              </Button>
              <Button size="sm" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
                Към таблото
              </Button>
            </div>
          </div>
        )}

        {/* Phenotype catalogue (browse) */}
        {!result && (
          <div className="mt-6">
            <h3 className="text-sm font-bold mb-2 opacity-70">Каталог: 8 метаболитни фенотипа</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(catalogue).map(([id, ph]) => (
                <div key={id} className={`p-3 rounded-xl border ${cardCls}`} data-testid={`catalogue-${id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{ph.icon}</span>
                    <span className="text-sm font-semibold" style={{ color: ph.color }}>{ph.label_bg}</span>
                  </div>
                  <p className="text-[11px] opacity-60 mt-1 leading-relaxed line-clamp-2">{ph.summary_bg}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
